import { Injectable } from '@angular/core';
import {
  Statement,
  Program,
  BlockStatement,
  Expression,
  Literal,
  SubExpression,
} from '../ast/ast-node.model';
import { BlockStatementNode } from '../ast/block-statement';
import { ContentStatementNode } from '../ast/content-statement';
import { MustacheStatementNode } from '../ast/mustache-statement';
import { PathExpressionNode } from '../ast/path-expression';
import { ProgramNode } from '../ast/program';
import { LiteralParser } from '../ast/utils/literal-parser';
import { templateEditorLogger } from '../logger.instance';

const logger = templateEditorLogger.extend('ast-parser');

@Injectable()
export class AstParserService {
  /**
   * Analisa uma string de template Handlebars e a converte em uma AST.
   */
  public parseStringToAst(template: string): Program {
    const statements: Statement[] = [];
    const stack: BlockStatement[] = [];

    const regex = /(?<tag>{{[^{}]*}})|(?<text>[^{]+|{(?!{))/g;
    let match;

    while ((match = regex.exec(template)) !== null) {
      const { tag, text } = match.groups!;
      logger.debug('Parsing match:', { tag, text });
      const parent = stack.length > 0 ? stack[stack.length - 1] : null;
      const targetBody = parent ? (parent.inverse ?? parent.program).body : statements;

      if (text) {
        targetBody.push(new ContentStatementNode(text));
        continue;
      }

      if (tag) {
        const content = tag.slice(2, -2).trim();

        if (content.startsWith('#')) {
          const parts = this.parseExpression(content.slice(1));
          const path = new PathExpressionNode(parts[0]);
          const params = parts.slice(1).map((prt) => this.createExpressionFromString(prt));
          const program = new ProgramNode([]);
          const block = new BlockStatementNode(path, params, program, null);
          targetBody.push(block);
          stack.push(block);
          continue;
        }

        if (content.startsWith('/')) {
          stack.pop();
          continue;
        }

        if (content === 'else') {
          if (parent) parent.inverse = new ProgramNode([]);
          continue;
        }

        const parts = this.parseExpression(content);
        const path = new PathExpressionNode(parts[0]);
        const params = parts.slice(1).map((prt) => this.createExpressionFromString(prt));
        targetBody.push(new MustacheStatementNode(path, params));
      }
    }

    const program = new ProgramNode(statements);
    logger.debug('parseStringToAst finished', { template, program });
    return program;
  }

  /**
   * Converte uma AST de volta para sua representação em string Handlebars.
   */
  public stringifyAst(program: Program): string {
    return program.body.map((node) => node.toString()).join('');
  }

  private createExpressionFromString(str: string): Expression | Literal | SubExpression {
    if (str.startsWith('(') && str.endsWith(')')) {
      const innerExpr = str.slice(1, -1);
      const parts = this.parseExpression(innerExpr);
      return {
        type: 'SubExpression',
        path: new PathExpressionNode(parts[0]),
        params: parts.slice(1).map((prt) => this.createExpressionFromString(prt)),
        toString: () => str,
      };
    }

    if (LiteralParser.isLiteral(str)) {
      return LiteralParser.parse(str);
    }

    return new PathExpressionNode(str);
  }

  private parseExpression(expression: string): string[] {
    const args: string[] = [];
    let currentItemIndex = 0;

    while (currentItemIndex < expression.length) {
      while (currentItemIndex < expression.length && /\s/.test(expression[currentItemIndex]))
        currentItemIndex++;
      if (currentItemIndex >= expression.length) break;

      const char = expression[currentItemIndex];

      if (char === '"' || char === "'") {
        const quote = char;
        let followingItemIndex = currentItemIndex + 1;
        while (followingItemIndex < expression.length && expression[followingItemIndex] !== quote)
          followingItemIndex++;
        args.push(expression.slice(currentItemIndex, followingItemIndex + 1));
        currentItemIndex = followingItemIndex + 1;
        continue;
      }

      if (char === '(') {
        let depth = 1;
        let followingItemIndex = currentItemIndex + 1;
        while (followingItemIndex < expression.length && depth > 0) {
          if (expression[followingItemIndex] === '(') depth++;
          else if (expression[followingItemIndex] === ')') depth--;
          followingItemIndex++;
        }

        const subExpr = expression.slice(currentItemIndex + 1, followingItemIndex - 1);
        args.push(`(${this.parseExpression(subExpr).join(' ')})`);
        currentItemIndex = followingItemIndex;
        continue;
      }

      let activeItemIndex = currentItemIndex;
      while (activeItemIndex < expression.length && !/[\s"'()]/.test(expression[activeItemIndex]))
        activeItemIndex++;
      args.push(expression.slice(currentItemIndex, activeItemIndex));
      currentItemIndex = activeItemIndex;
    }

    logger.debug('Parsed expression:', expression, '->', args);
    return args;
  }
}
